import { z } from 'zod'

/**
 * How a chart is laid out, on screen and on paper: one setting per
 * arrangement, so the sheet the band prints is the sheet they read on the
 * Stand. Stored as jsonb on the arrangement; anything missing or invalid
 * falls back to its default (a chart never fails to render over a setting).
 */

/**
 * The faces Planning Center offers for a chart, so an imported chart keeps
 * its look: Courier (its default), Arial/Helvetica, Monaco, Times New Roman
 * and Noto Sans (for scripts the others lack).
 */
export const CHART_FONTS = ['courier', 'helvetica', 'monaco', 'times', 'noto-sans'] as const
export type ChartFont = (typeof CHART_FONTS)[number]

/**
 * Fixed-width faces. A chart's source puts chords on their own line over the
 * lyric, lined up by column; only a fixed-width face shows that line-up as
 * the chart will read, so the editor types in the chart's face when it is one
 * of these, and in Courier otherwise.
 */
export const CHART_MONOSPACED: Record<ChartFont, boolean> = {
  courier: true,
  helvetica: false,
  monaco: true,
  times: false,
  'noto-sans': false,
}

/** The face the editor types in: the chart's own when fixed-width, else Courier. */
export const chartEditingFont = (font: ChartFont): ChartFont =>
  CHART_MONOSPACED[font] ? font : 'courier'

/** Names stored before the faces matched Planning Center's. */
const LEGACY_FONTS: Record<string, ChartFont> = {
  sans: 'helvetica',
  serif: 'times',
  mono: 'courier',
}

/**
 * Chord colours, a small palette that reads in both themes and in print.
 * `auto`: the brand colour on screen, black on paper.
 */
export const CHART_CHORD_COLORS = [
  'auto',
  'ink',
  'blue',
  'red',
  'green',
  'purple',
  'orange',
] as const
export type ChartChordColor = (typeof CHART_CHORD_COLORS)[number]

export const CHART_MARGINS = ['narrow', 'normal', 'wide'] as const
export type ChartMargins = (typeof CHART_MARGINS)[number]

export const CHART_ORIENTATIONS = ['portrait', 'landscape'] as const
export type ChartOrientation = (typeof CHART_ORIENTATIONS)[number]

export const CHART_FONT_SIZE = { min: 9, max: 20 } as const

export const chartLayoutSchema = z.object({
  font: z.enum(CHART_FONTS),
  /** Lyric size in points; chords are a point smaller. */
  fontSize: z.number().int().min(CHART_FONT_SIZE.min).max(CHART_FONT_SIZE.max),
  chordColor: z.enum(CHART_CHORD_COLORS),
  columns: z.union([z.literal(1), z.literal(2), z.literal(3)]),
  orientation: z.enum(CHART_ORIENTATIONS),
  margins: z.enum(CHART_MARGINS),
})

export type ChartLayout = z.infer<typeof chartLayoutSchema>

export const DEFAULT_CHART_LAYOUT: ChartLayout = {
  // Planning Center's default: what a chart typed with chords over lyrics
  // was lined up in.
  font: 'courier',
  fontSize: 12,
  chordColor: 'auto',
  columns: 1,
  orientation: 'portrait',
  margins: 'normal',
}

/**
 * A stored layout, made whole: each field that is missing or invalid takes
 * its default, and the rest are kept — a bad font never loses the columns.
 */
export function parseChartLayout(value: unknown): ChartLayout {
  const source: Record<string, unknown> =
    value && typeof value === 'object' && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : {}
  const legacy = typeof source.font === 'string' ? LEGACY_FONTS[source.font] : undefined
  const given: Record<string, unknown> = legacy ? { ...source, font: legacy } : source
  const shape = chartLayoutSchema.shape
  const field = <K extends keyof ChartLayout>(key: K): ChartLayout[K] => {
    const parsed = shape[key].safeParse(given[key])
    return parsed.success ? (parsed.data as ChartLayout[K]) : DEFAULT_CHART_LAYOUT[key]
  }
  return {
    font: field('font'),
    fontSize: field('fontSize'),
    chordColor: field('chordColor'),
    columns: field('columns'),
    orientation: field('orientation'),
    margins: field('margins'),
  }
}

/** A change to a layout: the fields given replace the stored ones (then made whole). */
export const chartLayoutPatchSchema = chartLayoutSchema.partial()
export type ChartLayoutPatch = z.infer<typeof chartLayoutPatchSchema>

/** Chord colours on paper, RGB 0–1. `auto` prints black, as a chart sheet does. */
export const CHORD_COLOR_RGB: Record<ChartChordColor, readonly [number, number, number]> = {
  auto: [0, 0, 0],
  ink: [0, 0, 0],
  blue: [0.15, 0.29, 0.78],
  red: [0.75, 0.11, 0.11],
  green: [0.05, 0.47, 0.25],
  purple: [0.43, 0.16, 0.85],
  orange: [0.8, 0.38, 0],
}

/** Page margins in points. */
export const CHART_MARGIN_POINTS: Record<ChartMargins, number> = {
  narrow: 36,
  normal: 54,
  wide: 72,
}
